import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { ICenterCLCTargetedPairsResponse } from '@nkr/store/index';
import { IClcScoresSummary } from '@nkr/models/index';
import { GridWrapper } from './styles';
import { AgGridReact, AgGridColumnProps, AgGridColumnGroupProps } from 'ag-grid-react';
import {
    IHeaderParams, GridApi, ColumnApi, GridReadyEvent,
    CsvExportParams, ValueGetterParams, ProcessCellForExportParams, FirstDataRenderedEvent, ICellRendererParams, GridSizeChangedEvent
} from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { FormControl } from 'react-bootstrap';

type MyColumn = AgGridColumnProps | AgGridColumnGroupProps;

interface IProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getCenterCLCTargetedPairsReport: () => Promise<ICenterCLCTargetedPairsResponse>
}

interface IState {
    report: IClcScoresSummary[]
}

class CenterCLCTargetedPairsGrid extends React.Component<IProps, {}> {
    readonly state: IState = {
        report: []
    }

    gridApi: GridApi = null;
    gridColumnApi: ColumnApi = null;

    onGridReady = (e: GridReadyEvent) => {
        this.gridApi = e.api;
        this.gridColumnApi = e.columnApi;
        e.api.sizeColumnsToFit();
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting report data, one moment please...').then(() => {
            this.props.getCenterCLCTargetedPairsReport()
                .then((response: ICenterCLCTargetedPairsResponse) => {
                    this.setState({
                        report: response.data.results
                    });

                    this.props.hideLoading();
                }).catch((ex) => {
                    alert('An error occured while loading report data');
                    this.props.hideLoading();
                });
        });
    }

    onGridSizeChanged = (e:GridSizeChangedEvent) => {
        // get the current grids width
        let gridWidth = document.getElementById('grid-wrapper').offsetWidth;

        // keep track of which columns to hide/show
        let columnsToShow = [];
        let columnsToHide = [];

        // iterate over all columns (visible or not) and work out
        // now many columns can fit (based on their minWidth)
        let totalColsWidth = 0;
        let allColumns = this.gridColumnApi.getAllColumns();

        for (let i = 0; i < allColumns.length; i++) {
            let column = allColumns[i];

            totalColsWidth += column.getMinWidth();

            if (totalColsWidth > gridWidth) {
                columnsToHide.push(column.getColId);
            } else {
                columnsToShow.push(column.getColId);
            }
        }

        // show/hide columns based on current grid width
        e.columnApi.setColumnsVisible(columnsToShow, true);
        e.columnApi.setColumnsVisible(columnsToHide, false);

        // fill out any available space to ensure there are no gaps
        e.api.sizeColumnsToFit();
    }

    handleDateChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;

        this.setState({
            [name]: value
        });
    }

    csvExport = () => {
        let params: CsvExportParams = {
            columnGroups: true,
            fileName: 'Center-Clc-Targeted-Pairs-Report.csv',
            processCellCallback: (params: ProcessCellForExportParams) => {
                let colDef = params.column.getColDef();

                if (colDef.headerName == 'Rank')
                    return params.node.rowIndex + 1;
                else if (colDef.colId == 'xpDate')
                    return moment(params.value).format('MM/DD/YYYY');

                return params.value;
            }
        };

        this.gridApi.exportDataAsCsv(params);
    }

    render = () => {
        const reportColumns: MyColumn[] = [
            {
                colId: 'Rank',
                field: 'rank',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        Rank
                    </SortableHeaderComponent>,
                headerName: 'Rank',
                // minWidth: 60,
                // maxWidth: 60,
                width: 60,
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                resizable: true,
                suppressSizeToFit: true,
                valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
            },
            {
                colId: 'ctrAlias',
                field: 'ctrAlias',
                headerName: 'Center Name',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        Center Name
                    </SortableHeaderComponent>,
                // minWidth: 120,
                // maxWidth: 140,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'patAlias',
                field: 'patAlias',
                headerName: 'Patient Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        Patient Alias
                    </SortableHeaderComponent>,
                // minWidth: 95,
                // maxWidth: 115,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'recipientABO',
                field: 'recipientABO',
                headerName: 'Blood Type',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        Blood Type
                    </SortableHeaderComponent>,
                // minWidth: 75,
                // maxWidth: 95,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'cprA4',
                field: 'cprA4',
                headerName: 'cPRA4',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        cPRA4
                    </SortableHeaderComponent>,
                // minWidth: 75,
                // maxWidth: 95,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'pairedDonorABO',
                field: 'pairedDonorABO',
                headerName: 'Paired Donor Blood Type',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        Paired Donor <br /> Blood Type
                    </SortableHeaderComponent>,
                // minWidth: 75,
                // maxWidth: 95,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'pairMatchPower',
                field: 'pairMatchPower',
                headerName: 'Pair Match Power',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        Pair Match<br />Power
                    </SortableHeaderComponent>,
                // minWidth: 75,
                // maxWidth: 95,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'patientOnDialysis',
                field: 'patientOnDialysis',
                headerName: 'Patient On Dialysis',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        Patient On <br />Dialysis
                    </SortableHeaderComponent>,
                // minWidth: 75,
                // maxWidth: 95,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'waitTimeNKR',
                field: 'waitTimeNKR',
                headerName: 'Wait Time In NKR',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        Wait Time<br />In NKR
                    </SortableHeaderComponent>,
                // minWidth: 75,
                // maxWidth: 95,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'clcu',
                field: 'clcu',
                headerName: 'CLCu',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) =>
                    <SortableHeaderComponent {...props}>
                        CLCu
                    </SortableHeaderComponent>,
                // minWidth: 75,
                // maxWidth: 95,
                sortable: true,
                // sort: 'asc',
                resizable: true
            }

        ];

        return (
            <>
                <div className='row'>
                    <div className='col-sm-12'>
                        <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap' style={{ marginBottom: 5 }}>
                            <AgGridReact
                                enableCellTextSelection={true}
                                enableRangeSelection={true}
                                columnDefs={reportColumns}
                                rowData={this.state.report}
                                headerHeight={40}
                                noRowsOverlayComponentFramework={() =>
                                    <div>No data for the selected date range...</div>}
                                onGridReady={this.onGridReady}
                                onGridSizeChanged={this.onGridSizeChanged}
                                onFirstDataRendered={(props: FirstDataRenderedEvent) => props.api.sizeColumnsToFit()}
                                suppressCellSelection={true}
                                onSortChanged={() => {
                                    this.gridApi.refreshCells();
                                }}
                            />
                        </GridWrapper>
                    </div>
                </div>
            </>
        );
    }
}

export default CenterCLCTargetedPairsGrid;